import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, ChevronRight, User, X, Search, ChevronLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useLocalize } from '@/hooks/useLandingContent';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { stripMarkdown } from '@/components/forum/Markdown';

const CATEGORIES = ['All', 'Buying', 'Selling', 'Renting', 'Investment', 'Market'];
const PAGE_SIZE = 9;
const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'most-viewed', label: 'Most viewed' },
] as const;
const RANGES = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
] as const;

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Blogs() {
  const localize = useLocalize();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') || '';
  const tagParam = params.get('tag') || '';
  const categoryParam = params.get('category') || '';
  const sort = (params.get('sort') as typeof SORTS[number]['value']) || 'newest';
  const range = (params.get('range') as typeof RANGES[number]['value']) || 'all';
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);

  const [search, setSearch] = useState(q);
  const debSearch = useDebounced(search, 300);

  // Sync debounced search → URL (and reset page)
  useEffect(() => {
    if (debSearch === q) return;
    const next = new URLSearchParams(params);
    if (debSearch) next.set('q', debSearch); else next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debSearch]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value && value !== 'all' && value !== 'All') next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams({}, { replace: true });

  // Pull distinct tags from published posts (best-effort, capped)
  const { data: tagPool } = useQuery({
    queryKey: ['blog-tag-pool'],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('tags')
        .eq('is_published', true)
        .limit(500);
      const set = new Set<string>();
      for (const r of data || []) for (const t of (r as any).tags || []) if (t) set.add(t);
      return [...set].sort();
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['blogs-list', { q: debSearch, categoryParam, tagParam, sort, range, page }],
    queryFn: async () => {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let req = supabase
        .from('blog_posts')
        .select('*', { count: 'exact' })
        .eq('is_published', true);

      if (categoryParam) req = req.eq('category', categoryParam);
      if (tagParam) req = req.contains('tags', [tagParam]);

      if (range !== 'all') {
        const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
        const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
        req = req.gte('published_at', since);
      }

      if (debSearch.trim()) {
        const term = debSearch.trim().replace(/[%,]/g, ' ');
        const like = `%${term}%`;
        req = req.or(
          `title->>en.ilike.${like},excerpt->>en.ilike.${like},body->>en.ilike.${like},category.ilike.${like}`
        );
      }

      switch (sort) {
        case 'oldest':
          req = req.order('published_at', { ascending: true });
          break;
        case 'most-viewed':
          req = req.order('view_count', { ascending: false }).order('published_at', { ascending: false });
          break;
        default:
          req = req.order('published_at', { ascending: false });
      }

      const { data, count, error } = await req.range(from, to);
      if (error) throw error;
      return { items: data || [], total: count || 0 };
    },
    placeholderData: (prev) => prev,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeCat = categoryParam || 'All';

  const fmtDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const readTime = (body: any) => {
    const text = typeof body === 'string' ? body : body?.en || '';
    const words = stripMarkdown(text).split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  };

  const hasFilters = !!(debSearch || categoryParam || tagParam || sort !== 'newest' || range !== 'all');

  const pageNumbers = useMemo(() => {
    const out: (number | 'gap')[] = [];
    const window = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - window && i <= page + window)) out.push(i);
      else if (out[out.length - 1] !== 'gap') out.push('gap');
    }
    return out;
  }, [page, totalPages]);

  return (
    <Layout>
      <SEOHead
        title="Real Estate Blogs — Ethio Real Estates"
        description="Insights, guides, and tips on buying, renting, and investing in Ethiopian real estate."
        canonical="/blogs"
      />
      <section className="bg-gradient-to-b from-muted/40 to-background py-12 border-b">
        <div className="container text-center">
          <div className="text-xs font-semibold tracking-[0.25em] text-primary uppercase mb-3">Our Blogs</div>
          <h1 className="font-display text-3xl md:text-5xl font-bold mb-4">Find our all blogs from here</h1>
          <p className="max-w-2xl mx-auto text-sm text-muted-foreground">
            Our blogs are written by industry researchers and well-known writers so you can read insights on Ethiopia's
            property market all in one place.
          </p>
        </div>
      </section>

      <section className="container py-10">
        {/* Search + filter bar */}
        <div className="sticky top-16 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 -mx-4 px-4 py-3 border-b mb-5">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search blog posts…"
                className="pl-9"
              />
            </div>
            <Select value={tagParam || 'all'} onValueChange={(v) => setParam('tag', v === 'all' ? null : v)}>
              <SelectTrigger className="md:w-[160px]"><SelectValue placeholder="All tags" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {(tagPool || []).map((t) => <SelectItem key={t} value={t}>#{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={(v) => setParam('range', v === 'all' ? null : v)}>
              <SelectTrigger className="md:w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => setParam('sort', v === 'newest' ? null : v)}>
              <SelectTrigger className="md:w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setParam('category', c === 'All' ? null : c)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                activeCat === c
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:border-primary/50'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Active filter chips */}
        {hasFilters && (
          <div className="mb-6 flex items-center gap-2 flex-wrap text-xs">
            <span className="text-muted-foreground">Active filters:</span>
            {debSearch && <FilterChip label={`"${debSearch}"`} onRemove={() => { setSearch(''); setParam('q', null); }} />}
            {categoryParam && <FilterChip label={categoryParam} onRemove={() => setParam('category', null)} />}
            {tagParam && <FilterChip label={`#${tagParam}`} onRemove={() => setParam('tag', null)} />}
            {sort !== 'newest' && <FilterChip label={SORTS.find((s) => s.value === sort)?.label || sort} onRemove={() => setParam('sort', null)} />}
            {range !== 'all' && <FilterChip label={RANGES.find((r) => r.value === range)?.label || range} onRemove={() => setParam('range', null)} />}
            <button onClick={clearAll} className="ml-1 underline text-primary">Clear all</button>
          </div>
        )}

        <div className="text-xs text-muted-foreground mb-4">
          {isLoading ? 'Searching…' : `${total} ${total === 1 ? 'post' : 'posts'} found`}
        </div>

        {isLoading ? (
          <div className="text-center py-16 text-muted-foreground">Loading blogs…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-4">No blog posts match these filters.</p>
            <Button variant="outline" onClick={clearAll}>Clear all filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p: any) => (
              <article
                key={p.id}
                className="rounded-xl overflow-hidden bg-card border hover:shadow-lg transition cursor-pointer group"
                onClick={() => navigate(`/blogs/${p.slug || p.id}`)}
              >
                <div className="aspect-[16/10] bg-muted overflow-hidden relative">
                  {p.cover_url && (
                    <img
                      src={optimizedImageUrl(p.cover_url, { width: 600, height: 380, quality: 75 })}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  )}
                  {p.category && (
                    <Badge className="absolute top-3 left-3 bg-primary/90 text-primary-foreground">{p.category}</Badge>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[11px] text-muted-foreground mb-2 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {p.author_name || 'Admin'}</span>
                    <span className="text-border">·</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(p.published_at)}</span>
                    <span className="text-border">·</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {readTime(p.body)} min read</span>
                  </div>
                  <h2 className="font-bold text-base leading-snug line-clamp-2 mb-2 group-hover:text-primary">
                    {localize(p.title)}
                  </h2>
                  <p className="text-xs text-muted-foreground line-clamp-3 mb-3">{localize(p.excerpt)}</p>
                  <span className="text-xs font-semibold text-primary inline-flex items-center gap-1">
                    Read more <ChevronRight className="h-3 w-3" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="flex items-center justify-center gap-1 mt-10" aria-label="Pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            {pageNumbers.map((n, i) =>
              n === 'gap' ? (
                <span key={`g${i}`} className="px-2 text-muted-foreground">…</span>
              ) : (
                <Button
                  key={n}
                  variant={n === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setParam('page', String(n))}
                  className="min-w-[36px]"
                >
                  {n}
                </Button>
              )
            )}
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setParam('page', String(page + 1))}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </nav>
        )}
      </section>
    </Layout>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1">
      {label}
      <button onClick={onRemove} className="ml-1" aria-label={`Remove ${label}`}>
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}
